import { create } from 'zustand';
import type { SceneSettingsPayloadData, SceneDeletedPayload, SceneMutatedPayload } from '@/bridge/features/sceneSettings';

interface SceneSettingsState {
  current: SceneSettingsPayloadData | null;
  /** floorType -> data-URL preview image, populated by the setFloorPreview bridge handler. */
  floorPreviews: Record<string, string>;
  /** skyPreviewKey(sceneId, locationType, timeType, lightingMode, floorType) -> data-URL preview
   *  image, populated by the setSkyPreview bridge handler. The sky render has no discrete catalog
   *  value to key on (Unity renders it from the scene's live lighting state), hence the composite
   *  key.
   *
   *  Deliberately NOT reset by clear(): clear() runs on every panel close, and re-rendering the sky
   *  on every reopen costs a Unity round trip for an image that almost never changed. The key is
   *  scene-id-scoped and scene ids are per-.previs, so the eviction that DOES matter is on project
   *  load — see the setSqlitePath handler in bridge/features/timeline.ts. The modal additionally
   *  evicts its own scene's entries when it comes back from the native lighting editor, where the
   *  render inputs can change without any key component moving. */
  skyPreviews: Record<string, string>;
  /** Fresh object per sceneDeleted echo (object-identity ack, same trick as `current`). */
  deleteResult: SceneDeletedPayload | null;
  /** Fresh object per sceneMutated echo — the board scene context menu's insert/duplicate/omit ack. */
  mutateResult: SceneMutatedPayload | null;
  setCurrent: (d: SceneSettingsPayloadData) => void;
  setDeleteResult: (d: SceneDeletedPayload) => void;
  clearDeleteResult: () => void;
  setMutateResult: (d: SceneMutatedPayload) => void;
  clearMutateResult: () => void;
  clear: () => void;
}

export const useSceneSettingsStore = create<SceneSettingsState>((set) => ({
  current: null,
  floorPreviews: {},
  skyPreviews: {},
  deleteResult: null,
  mutateResult: null,
  setCurrent: (d) => set({ current: d }),
  setDeleteResult: (d) => set({ deleteResult: d }),
  clearDeleteResult: () => set({ deleteResult: null }),
  setMutateResult: (d) => set({ mutateResult: d }),
  clearMutateResult: () => set({ mutateResult: null }),
  clear: () => set({ current: null, deleteResult: null, mutateResult: null }),
}));
